
import Link from '@/components/compat/Link';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowUpRight,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  FileText,
  FolderPlus,
  FolderTree,
  LayoutTemplate,
  Sparkles,
  Users,
} from 'lucide-react';
import { AceNavIcon } from '@/components/help-center/aces/AceIcon';
import { PowerNavIcon, UserMoreNavIcon } from '@/components/help-center/MaskedSvgIcon';
import { painelInsetCardClass } from '@/components/painel/painel-styles';
import type { PainelRole } from '@/lib/painel-rbac';
import { cn } from '@/lib/utils';

export type QuickActionDef = {
  href: string;
  label: string;
  sub: string;
  icon: LucideIcon;
  blob: string;
  ring: string;
};

const QUICK_ACTION_ICON_CLASS = cn(
  'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-950/50 bg-gradient-to-br ring-1',
  'shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
);

export const QUICK_ACTIONS: Record<string, QuickActionDef> = {
  newGuide: {
    href: '/painel/guias/new',
    label: 'Novo guia',
    sub: 'Artigo com blocos e menções a powers',
    icon: BookOpen,
    blob: 'from-sky-500/25 to-transparent',
    ring: 'ring-sky-400/30',
  },
  newPower: {
    href: '/painel/powers/new',
    label: 'Novo power',
    sub: 'Powerpédia, tutorial e preços',
    icon: PowerNavIcon,
    blob: 'from-sky-500/30 to-blue-950/20',
    ring: 'ring-sky-400/35',
  },
  newAce: {
    href: '/painel/aces',
    label: 'Novo ace',
    sub: 'Coleção e página pública',
    icon: AceNavIcon,
    blob: 'from-violet-500/22 to-transparent',
    ring: 'ring-violet-400/25',
  },
  newCategory: {
    href: '/painel/categorias',
    label: 'Nova categoria',
    sub: 'Organizar a base de conhecimento',
    icon: FolderTree,
    blob: 'from-cyan-500/20 to-transparent',
    ring: 'ring-cyan-400/25',
  },
  newUser: {
    href: '/painel/usuarios',
    label: 'Novo usuário',
    sub: 'Convidar membro da equipe',
    icon: UserMoreNavIcon,
    blob: 'from-emerald-500/20 to-transparent',
    ring: 'ring-emerald-400/25',
  },
  newPage: {
    href: '/painel/paginas',
    label: 'Nova página',
    sub: 'Institucional ou hub de conteúdo',
    icon: FileText,
    blob: 'from-indigo-500/25 to-transparent',
    ring: 'ring-indigo-400/25',
  },
  editHomepage: {
    href: '/painel/paginas/homepage',
    label: 'Editar homepage',
    sub: 'Hero e sugestões de pesquisa',
    icon: LayoutTemplate,
    blob: 'from-indigo-500/25 to-transparent',
    ring: 'ring-indigo-400/25',
  },
  reviewContent: {
    href: '/painel/revisao',
    label: 'Revisar conteúdo',
    sub: 'Guias, powers e aces aguardando aprovação',
    icon: ClipboardCheck,
    blob: 'from-sky-500/25 to-transparent',
    ring: 'ring-sky-400/30',
  },
  approvePublish: {
    href: '/painel/revisao',
    label: 'Aprovar publicação',
    sub: 'Publicar após revisão',
    icon: CheckCircle2,
    blob: 'from-emerald-500/22 to-transparent',
    ring: 'ring-emerald-400/25',
  },
};

export const ROLE_QUICK_ACTION_KEYS: Record<PainelRole, (keyof typeof QUICK_ACTIONS)[]> = {
  super_admin: ['newGuide', 'newPower', 'newAce', 'newCategory', 'newUser'],
  admin: ['newGuide', 'newPower', 'newAce', 'newCategory', 'newUser'],
  editor: ['newGuide', 'newPower', 'newPage'],
  reviewer: ['reviewContent', 'approvePublish'],
};

type PainelRoleQuickActionsProps = {
  role: PainelRole;
};

export function PainelRoleQuickActions({ role }: PainelRoleQuickActionsProps) {
  const actionEntries = ROLE_QUICK_ACTION_KEYS[role].map((key) => [key, QUICK_ACTIONS[key]] as const);

  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-poppins text-lg font-bold text-white">Ações rápidas</h2>
        <p className="mt-0.5 font-inter text-xs text-slate-400">Atalhos para as tarefas mais comuns</p>
      </div>
      <div
        className={cn(
          'grid gap-3',
          actionEntries.length <= 3 ? 'sm:grid-cols-2 xl:grid-cols-3' : 'sm:grid-cols-2 xl:grid-cols-4'
        )}
      >
        {actionEntries.map(([actionKey, { href, label, sub, icon: Icon, blob, ring }]) => (
          <Link
            key={actionKey}
            href={href}
            className={cn(
              painelInsetCardClass,
              'group relative overflow-hidden p-5 transition-all duration-300',
              'hover:border-sky-400/40 hover:bg-white/[0.07] hover:shadow-[0_0_36px_-8px_rgba(56,189,248,0.22)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50'
            )}
          >
            <div
              className={cn(
                'pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br opacity-60 blur-2xl transition-opacity group-hover:opacity-90',
                blob
              )}
              aria-hidden
            />
            <div className="relative flex items-start justify-between gap-3">
              <div className={cn(QUICK_ACTION_ICON_CLASS, blob, ring)}>
                <Icon className="h-5 w-5 text-sky-100" />
              </div>
              <ArrowUpRight className="h-4 w-4 shrink-0 text-blue-300/40 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-sky-300" />
            </div>
            <p className="relative mt-4 font-poppins text-sm font-semibold text-white">{label}</p>
            <p className="relative mt-1 font-inter text-xs text-slate-400">{sub}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

type PainelRoleWelcomeHeaderProps = {
  role: PainelRole;
  name: string;
};

const ROLE_WELCOME: Record<PainelRole, { subtitle: string; ctas: (keyof typeof QUICK_ACTIONS)[] }> = {
  super_admin: {
    subtitle: 'Controle total do xat Ajuda — conteúdo, equipe, analytics e configurações.',
    ctas: ['newGuide', 'newCategory', 'editHomepage'],
  },
  admin: {
    subtitle: 'Gerencie guias, powers, aces, páginas, changelogs e a equipe do portal.',
    ctas: ['newGuide', 'newCategory', 'editHomepage'],
  },
  editor: {
    subtitle: 'Produza e refine guias, powers, aces e páginas. Seus rascunhos ficam salvos localmente.',
    ctas: ['newGuide', 'newPower', 'newPage'],
  },
  reviewer: {
    subtitle: 'Revise, aprove e publique conteúdos antes de irem ao ar.',
    ctas: ['reviewContent', 'approvePublish'],
  },
};

export function PainelRoleWelcomeHeader({ role, name }: PainelRoleWelcomeHeaderProps) {
  const config = ROLE_WELCOME[role];

  return (
    <section
      className={cn(
        'relative overflow-hidden px-6 py-8 md:px-10 md:py-10',
        'rounded-2xl border border-sky-400/25 bg-gradient-to-br from-[#050032]/90 via-[#0a1468]/35 to-[#030023]/95',
        'shadow-[0_0_60px_-12px_rgba(56,189,248,0.25)] backdrop-blur-md'
      )}
    >
      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" aria-hidden />
      <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 space-y-2">
          <p className="font-inter text-xs font-medium uppercase tracking-[0.2em] text-sky-300/70">Painel</p>
          <h1 className="font-poppins text-3xl font-bold tracking-tight text-white md:text-4xl">
            Olá, {name}
          </h1>
          <p className="max-w-xl font-inter text-sm leading-relaxed text-blue-100/75 md:text-[15px]">
            {config.subtitle}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {config.ctas.map((actionKey, i) => {
            const action = QUICK_ACTIONS[actionKey];
            const Icon = action.icon;
            return (
              <Link
                key={actionKey}
                href={action.href}
                className={cn(
                  'inline-flex h-10 items-center justify-center rounded-xl px-4 font-poppins text-sm font-semibold transition-all duration-200',
                  i === 0
                    ? 'border border-sky-400/50 bg-sky-500/90 text-white shadow-lg shadow-sky-950/40 hover:bg-sky-400'
                    : 'border border-white/20 bg-white/[0.06] text-blue-50 hover:border-sky-400/40 hover:bg-white/10'
                )}
              >
                <Icon className="mr-2 h-4 w-4" />
                {action.label}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export const ROLE_STAT_KEYS: Record<
  PainelRole,
  Array<'guides' | 'powers' | 'aces' | 'users' | 'views' | 'categories' | 'drafts' | 'review' | 'published'>
> = {
  super_admin: ['guides', 'powers', 'aces', 'users', 'views', 'categories', 'drafts'],
  admin: ['guides', 'powers', 'aces', 'users', 'views', 'categories', 'drafts'],
  editor: ['guides', 'powers', 'drafts'],
  reviewer: ['review', 'published', 'guides'],
};

export const STAT_META = {
  guides: { title: 'Guias', icon: BookOpen, variant: 'sky' as const },
  powers: { title: 'Powers', icon: PowerNavIcon, variant: 'violet' as const },
  aces: { title: 'Aces', icon: AceNavIcon, variant: 'violet' as const },
  users: { title: 'Usuários', icon: Users, variant: 'violet' as const },
  views: { title: 'Visualizações', icon: Eye, variant: 'indigo' as const },
  categories: { title: 'Categorias', icon: FolderTree, variant: 'cyan' as const },
  drafts: { title: 'Rascunhos', icon: FileText, variant: 'cyan' as const },
  review: { title: 'Aguardando revisão', icon: ClipboardCheck, variant: 'cyan' as const },
  published: { title: 'Publicados recentemente', icon: Sparkles, variant: 'sky' as const },
};
